/**
 * THE ONE PLACE THAT ANSWERS "WHAT IS THIS PERSON, AND WHAT MAY THEY DO?"
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * `memberships.role` was carrying two unrelated ideas in one scalar:
 *
 *   AUTHORITY / ACCESS   OWNER, MANAGER, ORG_ADMIN, REGION_MANAGER,
 *                        DISTRICT_MANAGER, AUDITOR
 *   OPERATIONAL WORK     TECHNICIAN, SERVICE_WRITER, DISPATCHER, PARTS
 *
 * A single value cannot say "owner who also turns wrenches", so the product
 * could only represent that by duplicating the human — a second membership, or
 * a second professional identity. Both split one person in half.
 *
 *   One person. One membership in a shop. One professional identity.
 *   Multiple operational work roles.
 *
 * Authority stays SINGULAR. Work identity becomes a SET.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY IT IS ONE MODULE AND NOT A FALLBACK SPRINKLED EVERYWHERE
 * ═══════════════════════════════════════════════════════════════════════════
 * During compatibility, some rows have the new columns and some are read
 * through the legacy `role`. If each call site invented its own
 * "authority_role ?? derive from role" the two would drift, and they would
 * drift silently — the kind of divergence where the nav says one thing and the
 * server says another. Every consumer resolves through `resolveMembershipRoles`
 * so there is exactly one legacy-compatibility rule to read, test and delete.
 *
 * DELETING IT LATER: once every membership row is populated and every writer
 * sets the new columns, `deriveLegacyAuthority` / `deriveLegacyWorkRoles` are
 * the only things that need to go.
 */

// ───────────────────────────────────────────────────────────────────────────
// Vocabulary
// ───────────────────────────────────────────────────────────────────────────

/** What a person may ACCESS. Singular per membership. */
export type AuthorityRole =
  | 'STAFF'
  | 'AUDITOR'
  | 'MANAGER'
  | 'DISTRICT_MANAGER'
  | 'REGION_MANAGER'
  | 'ORG_ADMIN'
  | 'OWNER';

/** What a person DOES. Zero or many per membership. */
export type WorkRole =
  | 'TECHNICIAN'
  | 'SERVICE_ADVISOR'
  | 'DISPATCHER'
  | 'PARTS'
  | 'DETAIL_SPECIALIST';

/** What a work role may be assigned to work on. */
export type WorkFamily = 'MECHANICAL' | 'DETAIL';

export const AUTHORITY_ROLES: readonly AuthorityRole[] = [
  'STAFF', 'AUDITOR', 'MANAGER', 'DISTRICT_MANAGER', 'REGION_MANAGER', 'ORG_ADMIN', 'OWNER',
] as const;

export const WORK_ROLES: readonly WorkRole[] = [
  'TECHNICIAN', 'SERVICE_ADVISOR', 'DISPATCHER', 'PARTS', 'DETAIL_SPECIALIST',
] as const;

/** The label a human sees. `DETAIL_SPECIALIST` is never shown raw. */
export const WORK_ROLE_LABELS: Readonly<Record<WorkRole, string>> = {
  TECHNICIAN: 'Technician',
  SERVICE_ADVISOR: 'Service Advisor',
  DISPATCHER: 'Dispatcher',
  PARTS: 'Parts',
  DETAIL_SPECIALIST: 'Detail Specialist',
};

export const AUTHORITY_LABELS: Readonly<Record<AuthorityRole, string>> = {
  STAFF: 'Staff',
  AUDITOR: 'Auditor',
  MANAGER: 'Manager',
  DISTRICT_MANAGER: 'District Manager',
  REGION_MANAGER: 'Region Manager',
  ORG_ADMIN: 'Org Admin',
  OWNER: 'Owner',
};

// ───────────────────────────────────────────────────────────────────────────
// Legacy compatibility — the ONLY place the old scalar is interpreted
// ───────────────────────────────────────────────────────────────────────────

/**
 * Authority implied by a legacy `app_role`.
 *
 * Everything that is not an access role is STAFF. A technician is not a lesser
 * manager — they are staff who turn wrenches, and the WORK role is what says
 * so. Unknown values also land on STAFF, so a value this build has never seen
 * can never read as elevated.
 */
export function deriveLegacyAuthority(legacyRole: string | null | undefined): AuthorityRole {
  switch ((legacyRole ?? '').toUpperCase()) {
    case 'OWNER': return 'OWNER';
    case 'ORG_ADMIN': return 'ORG_ADMIN';
    case 'REGION_MANAGER': return 'REGION_MANAGER';
    case 'DISTRICT_MANAGER': return 'DISTRICT_MANAGER';
    case 'MANAGER': return 'MANAGER';
    case 'AUDITOR': return 'AUDITOR';
    default: return 'STAFF';
  }
}

/**
 * Work roles implied by a legacy `app_role`.
 *
 * Only the four operational values imply one, and each implies exactly itself.
 * An OWNER row says this person has owner ACCESS and says nothing at all about
 * whether they turn wrenches — so it yields NOTHING here. Guessing TECHNICIAN
 * for every owner would fabricate an operational identity and, worse, would
 * make them eligible for mechanical assignment. An empty set is the truthful
 * answer, and the owner gains TECHNICIAN when a human says so.
 */
export function deriveLegacyWorkRoles(legacyRole: string | null | undefined): WorkRole[] {
  switch ((legacyRole ?? '').toUpperCase()) {
    case 'TECHNICIAN': return ['TECHNICIAN'];
    case 'SERVICE_WRITER': return ['SERVICE_ADVISOR'];
    case 'SERVICE_ADVISOR': return ['SERVICE_ADVISOR'];
    case 'DISPATCHER': return ['DISPATCHER'];
    case 'PARTS': return ['PARTS'];
    default: return [];
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Resolution
// ───────────────────────────────────────────────────────────────────────────

export interface MembershipRoleInput {
  /** Legacy scalar. Still populated and still read during compatibility. */
  role?: string | null;
  /** New singular authority. Absent on rows written before the migration. */
  authority_role?: AuthorityRole | string | null;
  /** New work-role set. Absent (not empty) when it has not been loaded. */
  work_roles?: readonly (WorkRole | string)[] | null;
  /**
   * When somebody last STATED this membership's work-role set.
   *
   * This is what tells an empty set apart from an unanswered one, and the
   * two need opposite treatment: an unanswered membership falls back to the
   * legacy scalar (every migrated shop depends on that), while a stated
   * empty set means the person performs no operational work and must be
   * offered none.
   *
   * `null`/absent means nobody has stated it. Mirrors
   * `memberships.work_roles_initialized_at`.
   */
  work_roles_initialized_at?: string | Date | null;
}

export interface ResolvedMembershipRoles {
  authority: AuthorityRole;
  workRoles: readonly WorkRole[];
  /** True when either half had to be derived from the legacy scalar. */
  usedLegacyFallback: boolean;
  /**
   * Whether somebody has actually stated this person's work-role set.
   *
   * False means the set is UNKNOWN, not empty — the legacy scalar answers for
   * them. True with an empty `workRoles` means somebody said "none", and it
   * must be honoured rather than filled in.
   */
  workRolesStated: boolean;
}

const isAuthority = (v: unknown): v is AuthorityRole =>
  typeof v === 'string' && (AUTHORITY_ROLES as readonly string[]).includes(v);

const isWorkRole = (v: unknown): v is WorkRole =>
  typeof v === 'string' && (WORK_ROLES as readonly string[]).includes(v);

/**
 * The canonical answer. Prefers the new columns; falls back to the legacy
 * scalar only where the new data is genuinely absent.
 *
 * Note the asymmetry on work roles, which is deliberate: `null`/`undefined`
 * means "not loaded" and falls back, while an EMPTY ARRAY means "loaded, and
 * this person performs no operational work" and is respected. Without that
 * distinction, revoking a person's last work role would silently resurrect the
 * legacy one.
 */
export function resolveMembershipRoles(m: MembershipRoleInput | null | undefined): ResolvedMembershipRoles {
  if (!m) {
    return {
      authority: 'STAFF', workRoles: [],
      usedLegacyFallback: false, workRolesStated: false,
    };
  }

  let usedLegacyFallback = false;

  const authority: AuthorityRole = isAuthority(m.authority_role)
    ? m.authority_role
    : (usedLegacyFallback = true, deriveLegacyAuthority(m.role));

  let workRoles: WorkRole[];
  if (m.work_roles == null) {
    usedLegacyFallback = true;
    workRoles = deriveLegacyWorkRoles(m.role);
  } else {
    workRoles = m.work_roles.filter(isWorkRole);
  }

  // HAS ANYBODY ACTUALLY ANSWERED?
  //
  // Read off the INPUT, never off the resolved set. A legacy membership whose
  // `work_roles` were never loaded resolves to a role DERIVED from the scalar,
  // and a derivation is not a statement — nobody said it, the scalar implied
  // it. Reading the resolved set would call that derived role an answer and
  // lose the very distinction this exists to make.
  //
  // Holding a real row IS an answer: somebody granted it. Otherwise only the
  // timestamp can say so, because an empty set writes no row and leaves no
  // other trace. Mirrors the database, where `membership_work_roles` rows and
  // `work_roles_initialized_at` carry exactly these two signals.
  const workRolesStated =
    (m.work_roles != null && m.work_roles.length > 0) ||
    m.work_roles_initialized_at != null;

  // Stable order, so UI lists and test expectations do not depend on insert
  // order or on what the database happened to return first.
  const order = new Map(WORK_ROLES.map((r, i) => [r, i]));
  workRoles = [...new Set(workRoles)].sort((a, b) => (order.get(a)! - order.get(b)!));

  return { authority, workRoles, usedLegacyFallback, workRolesStated };
}

// ───────────────────────────────────────────────────────────────────────────
// Capabilities
// ───────────────────────────────────────────────────────────────────────────

// NOTE — there is deliberately NO authority ranking here.
//
// An earlier draft of this module exported `hasAtLeastAuthority` over a numeric
// ladder. A ladder is the wrong shape: AUDITOR is READ-ONLY rather than "above
// STAFF", and REGION_MANAGER / DISTRICT_MANAGER are SCOPE-BOUND rather than
// stronger managers. Comparing them numerically invites exactly the bug where
// an auditor mutates something because they outrank somebody.
//
// Authority questions belong in ./authority, which answers them as explicit
// capabilities plus scope:
//
//     canPerformAuthorityAction(member, 'manage_team', { shopId })
//
// Nothing in this module can express an authority decision, which is what
// makes "a work role granted admin rights" unwritable rather than merely
// discouraged.

export function hasWorkRole(
  m: MembershipRoleInput | null | undefined,
  role: WorkRole,
): boolean {
  return resolveMembershipRoles(m).workRoles.includes(role);
}

/**
 * Which families this person may be assigned. Mirrors
 * `public.work_role_families`; the database remains authoritative and this
 * exists so the UI can avoid offering work the server would refuse.
 */
export const WORK_ROLE_FAMILIES: Readonly<Record<WorkRole, readonly WorkFamily[]>> = {
  TECHNICIAN: ['MECHANICAL'],
  SERVICE_ADVISOR: [],
  DISPATCHER: [],
  PARTS: [],
  DETAIL_SPECIALIST: ['DETAIL'],
};

export function eligibleWorkFamilies(
  m: MembershipRoleInput | null | undefined,
): readonly WorkFamily[] {
  // NO LEGACY FALLBACK HERE, deliberately.
  //
  // This answers "what does this membership say this person does", and an
  // empty answer is empty. The conservative legacy fallback — treat an
  // undescribed person as MECHANICAL so a migrated shop's board does not
  // empty — belongs to `assignmentEligibility`, which is about ROSTER
  // CANDIDATES. Applying it here would make every owner who has never been
  // given a work role eligible for mechanical work merely by owning the shop,
  // which is precisely what the foundation backfill refused to do.
  const { workRoles } = resolveMembershipRoles(m);

  const out = new Set<WorkFamily>();
  for (const r of workRoles) for (const f of WORK_ROLE_FAMILIES[r]) out.add(f);
  return [...out];
}

/**
 * Categories that belong to DETAIL. Everything else is MECHANICAL — including
 * anything added to the catalog tomorrow.
 *
 * Fail-closed on purpose, and in the direction that matters: an unclassified
 * category must never become detail work by accident, because that is the
 * direction that would hand a detail-only worker a brake job. Mirrors
 * `public.work_family_categories`.
 */
const DETAIL_CATEGORY_KEYS: ReadonlySet<string> = new Set([
  'detailing', 'wash', 'interior detail', 'exterior detail', 'paint correction',
]);

export function categoryWorkFamily(category: string | null | undefined): WorkFamily {
  return DETAIL_CATEGORY_KEYS.has((category ?? '').trim().toLowerCase())
    ? 'DETAIL'
    : 'MECHANICAL';
}

/**
 * Assignment eligibility.
 *
 * A membership with NO work roles is eligible for nothing — eligibility is
 * something a person is given, never something they have by default. That is
 * why an OWNER who has not been made a technician cannot be handed a brake job
 * merely for being the owner.
 */
export function canPerformCategory(
  m: MembershipRoleInput | null | undefined,
  category: string | null | undefined,
): boolean {
  return eligibleWorkFamilies(m).includes(categoryWorkFamily(category));
}

// ───────────────────────────────────────────────────────────────────────────
// Metrics partitioning
// ───────────────────────────────────────────────────────────────────────────

/**
 * Which work role an action should be RECORDED against.
 *
 * Possessing a role must not pollute its metrics — Andy changing shop settings
 * is not technician work, and a detailer washing a car is not mechanical
 * proficiency. So the performed role comes from the ACTION, and the person's
 * role set is used only to confirm they were entitled to perform it.
 *
 * Returns null when the action does not belong to any operational role (an
 * authority action such as editing shop settings), or when the person does not
 * hold the role the action implies — in which case the caller should persist
 * nothing rather than attribute it to the wrong stream.
 */
export function performedWorkRoleForCategory(
  m: MembershipRoleInput | null | undefined,
  category: string | null | undefined,
): WorkRole | null {
  const { workRoles } = resolveMembershipRoles(m);
  const family = categoryWorkFamily(category);
  const candidates = workRoles.filter((r) => WORK_ROLE_FAMILIES[r].includes(family));
  // Exactly one work role maps to each family today, so this is unambiguous.
  // If that ever stops being true the caller must persist an explicit snapshot
  // rather than let this guess.
  return candidates.length === 1 ? candidates[0] : null;
}

// ───────────────────────────────────────────────────────────────────────────
// The compatibility bridge
// ───────────────────────────────────────────────────────────────────────────

/**
 * The set of legacy `app_role` values this membership BEHAVES AS.
 *
 * ~90 migrations' worth of RLS policies, guard triggers and permission
 * predicates ask "is this person a TECHNICIAN / SERVICE_WRITER / MANAGER?".
 * A new multi-role member carries the neutral legacy value STAFF, so asking
 * that question of `membership.role` alone would answer "none of them" and
 * lock a working technician out of the ticket list.
 *
 * This projects authority + work roles back onto the old vocabulary so those
 * consumers keep working while they are migrated one at a time.
 *
 * TWO MAPPINGS ARE DELIBERATELY ABSENT, and they are the point:
 *
 *   DETAIL_SPECIALIST -> nothing. There is no legacy value that means
 *   "detailer", and the only near-miss, TECHNICIAN, is exactly the conflation
 *   this campaign exists to end. A detailer projecting as TECHNICIAN would be
 *   handed mechanical work by every legacy path. So they project as nothing,
 *   and legacy technician-gated surfaces correctly exclude them.
 *
 *   STAFF authority -> nothing. Being a member grants no legacy capability by
 *   itself; what you may DO comes from your work roles.
 */
export function effectiveLegacyRoles(m: MembershipRoleInput | null | undefined): readonly string[] {
  if (!m) return [];
  const { authority, workRoles } = resolveMembershipRoles(m);
  const out = new Set<string>();

  // Authority projects onto its own legacy name, where one exists.
  if (authority !== 'STAFF') out.add(authority);

  for (const r of workRoles) {
    switch (r) {
      case 'TECHNICIAN': out.add('TECHNICIAN'); break;
      // The legacy scalar spells this one differently.
      case 'SERVICE_ADVISOR': out.add('SERVICE_WRITER'); break;
      case 'DISPATCHER': out.add('DISPATCHER'); break;
      case 'PARTS': out.add('PARTS'); break;
      case 'DETAIL_SPECIALIST': /* intentionally nothing — see above */ break;
    }
  }

  // A legacy row whose scalar is still the source of truth keeps working even
  // if it names something this build does not model.
  const raw = (m.role ?? '').toUpperCase();
  if (raw && raw !== 'STAFF') out.add(raw);

  return [...out];
}

/**
 * Legacy-compatible membership test. Replaces `allowed.includes(membership.role)`
 * at call sites that have not yet been migrated to capabilities or work roles.
 */
export function actsAsLegacyRole(
  m: MembershipRoleInput | null | undefined,
  ...allowed: string[]
): boolean {
  const effective = effectiveLegacyRoles(m);
  return allowed.some((a) => effective.includes(a.toUpperCase()));
}

/**
 * Which legacy `app_role` value to WRITE for a membership.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS NOT SIMPLY "STAFF FOR EVERYONE NEW"
 * ═══════════════════════════════════════════════════════════════════════════
 * `memberships.role` is NOT NULL and is still read by roughly ninety
 * migrations' worth of policies and guard triggers. Most of them ask
 * "is this person X?" and deny when the answer is no, so an unrecognised
 * value is harmless there.
 *
 * But `guard_technician_ticket_update` is shaped the other way round:
 *
 *     IF v_role != 'TECHNICIAN' THEN
 *       RETURN NEW;            -- not a technician: allow everything
 *     END IF;
 *     ... freeze 11 business fields ...
 *
 * Not being a technician SKIPS a restriction. So writing STAFF for somebody
 * who really does turn wrenches would quietly release them from the technician
 * field-freeze — a regression, and exactly the kind that hides until a
 * technician edits something they should not have been able to.
 *
 * So the legacy scalar keeps naming the person's most significant legacy
 * identity, and STAFF is reserved for the people who genuinely have no legacy
 * equivalent — a pure owner with no operational role, or a Detail Specialist.
 * For those, every legacy check correctly denies, and the freeze they skip is
 * one that governs a ticket update RLS never grants them in the first place.
 *
 * DETAIL_SPECIALIST NEVER produces TECHNICIAN here. That substitution is the
 * whole thing this campaign exists to prevent.
 */
export function legacyScalarFor(
  authority: AuthorityRole,
  workRoles: readonly WorkRole[],
): string {
  // Authority above STAFF keeps its own name, exactly as today. An owner who
  // also turns wrenches stays OWNER in the legacy scalar, which is what they
  // would have been before this campaign — no behaviour changes for them.
  if (authority !== 'STAFF') return authority;

  // Otherwise the most restrictive legacy operational identity they hold, so
  // guards that key off it still apply. TECHNICIAN first, deliberately: it is
  // the one that ADDS a restriction.
  if (workRoles.includes('TECHNICIAN')) return 'TECHNICIAN';
  if (workRoles.includes('SERVICE_ADVISOR')) return 'SERVICE_WRITER';
  if (workRoles.includes('DISPATCHER')) return 'DISPATCHER';
  if (workRoles.includes('PARTS')) return 'PARTS';

  // DETAIL_SPECIALIST and "no work roles" both land here. Neutral by design.
  return 'STAFF';
}
